'use client';

import { useId, useState } from 'react';

import { ValidationError } from '@eliadmin/core/src/api/errors';
import type { Customer } from '@eliadmin/core/src/api/customers/customers-types';
import { newsletterSubscribe } from '@eliadmin/core/src/api/newsletter/newsletter-api';
import { AccountCard } from '@eliadmin/core/src/components/nt/account/shell/account-card';
import { ConfirmDialog } from '@eliadmin/core/src/components/nt/account/profile/confirm-dialog';
import { Toggle } from '@eliadmin/core/src/components/nt/forms/controls';
import { useAccountTexts } from '@eliadmin/core/src/components/nt/shared/site-texts';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { initCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';

type NewsletterState =
  | { status: 'idle' | 'subscribing' | 'pending' | 'unsubscribed' }
  | { status: 'failed'; message: string };

/**
 * Figma „Pref · Novinky a zľavy na tonery e-mailom“ (205:11129): Body/L Bold title over the
 * Body/M #363D36 text, the toggle on the right, padding 14 / 0, gap 24. Bound to
 * `newsletter_subscribed` with the behaviour of the core: switching on subscribes the account
 * e-mail (double opt-in – the subscription starts with the confirmation link), switching off asks
 * first and opens the customer's `newsletter_unsubscribe_url` (core `CancelSubscriptionModal`).
 * Without that link the API offers no way out, so the toggle stays on and disabled.
 */
function NewsletterSetting({ customer, authToken }: { customer: Customer; authToken: string }) {
  const { t } = useTranslations();
  const texts = useAccountTexts();
  const locale = useLocaleStore(s => s.currentLocale);
  const titleId = useId();
  const textId = useId();
  const [state, setState] = useState<NewsletterState>({ status: 'idle' });
  const [confirming, setConfirming] = useState(false);
  const subscribed = !!customer.newsletter_subscribed;
  const unsubscribeUrl = customer.newsletter_unsubscribe_url;

  const subscribe = async () => {
    setState({ status: 'subscribing' });
    try {
      await newsletterSubscribe({
        locale,
        body: {
          email: customer.email,
          gdpr: true,
          return_url: new URL(ROUTES.NEWSLETTER_CONFIRM(), env.NEXT_PUBLIC_APP_URL).toString(),
        },
      });
      setState({ status: 'pending' });
    } catch (error) {
      const message = error instanceof ValidationError ? error.fields.email?.join(' ') : '';
      setState({ status: 'failed', message: message || t('text573') });
    }
  };

  const unsubscribe = () => {
    initCustomerStore({ authToken, customer: { ...customer, newsletter_subscribed: false } });
    setState({ status: 'unsubscribed' });
    setConfirming(false);
  };

  const text =
    state.status === 'failed'
      ? state.message
      : state.status === 'pending'
        ? t('text576')
        : state.status === 'unsubscribed'
          ? t('text3225')
          : texts.newsletterText;

  return (
    <div className="flex items-center gap-6 py-3.5">
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p id={titleId} className="nt-body-l-bold text-[#0D110D]">
          {texts.newsletter}
        </p>
        {text ? (
          <p
            id={textId}
            aria-live="polite"
            className={cn(
              'nt-body-m',
              state.status === 'failed' ? 'text-[#B42318]' : 'text-[#363D36]',
            )}
          >
            {text}
          </p>
        ) : null}
      </div>
      <Toggle
        aria-labelledby={titleId}
        aria-describedby={text ? textId : undefined}
        aria-busy={state.status === 'subscribing' || undefined}
        checked={subscribed || state.status === 'pending'}
        disabled={
          state.status === 'subscribing' ||
          state.status === 'pending' ||
          (subscribed && !unsubscribeUrl)
        }
        onChange={event => (event.target.checked ? subscribe() : setConfirming(true))}
      />
      {unsubscribeUrl ? (
        <ConfirmDialog
          open={confirming}
          onOpenChange={setConfirming}
          title={t('text3226')}
          description={t('text3224')}
          confirm={
            <Button asChild size="md" iconRight="arrow-up-right">
              <a
                href={unsubscribeUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={unsubscribe}
              >
                {t('text3223')}
              </a>
            </Button>
          }
        />
      ) : null}
    </div>
  );
}

/**
 * Figma „Card · Komunikácia a súhlasy“ (205:11129). Only the newsletter is backed by customer data;
 * the designed „SMS o stave objednávky“, „Pripomienka, keď sa vám míňa toner“ and „Dotazník
 * spokojnosti po nákupe“ have no field in the API and are not shown. Without newsletter data the
 * card is left out.
 */
export function CommunicationCard({
  customer,
  authToken,
}: {
  customer: Customer;
  authToken: string;
}) {
  const texts = useAccountTexts();
  if (typeof customer.newsletter_subscribed !== 'boolean' || !texts.newsletter) return null;

  return (
    <AccountCard title={texts.communication}>
      <NewsletterSetting customer={customer} authToken={authToken} />
    </AccountCard>
  );
}
